import { useState } from "react";
import { photos } from "../data/property";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { trackEvent } from "../lib/analytics";
import SunFlare from "./SunFlare";
import Popcorn from "./Popcorn";

const NETFLIX_PHOTO_INDEX = 4;

// Curated bento layout (12-column grid). `outdoor` tiles get a warm sun-light overlay.
const layout = [
  { idx: 1, span: "md:col-span-8 md:row-span-2", aspect: "aspect-[16/10]", outdoor: true },
  { idx: 14, span: "md:col-span-4", aspect: "aspect-[4/5]" },
  { idx: 8, span: "md:col-span-4", aspect: "aspect-[4/5]", outdoor: true },
  { idx: 5, span: "md:col-span-4", aspect: "aspect-[4/5]" },
  { idx: 11, span: "md:col-span-4", aspect: "aspect-[4/5]", outdoor: true },
  { idx: 32, span: "md:col-span-4", aspect: "aspect-[4/5]", outdoor: true },
  { idx: 23, span: "md:col-span-6", aspect: "aspect-[3/2]", outdoor: true },
  { idx: 31, span: "md:col-span-6", aspect: "aspect-[3/2]" },
  { idx: 17, span: "md:col-span-12", aspect: "aspect-[16/9]", outdoor: true }
];

export default function Gallery() {
  const [lightbox, setLightbox] = useState(null);

  const open = (i) => { trackEvent("gallery_open", { photo_index: i }); setLightbox(i); };
  const close = () => setLightbox(null);
  const prev = (e) => {
    e?.stopPropagation();
    setLightbox((i) => (i === 0 ? photos.length - 1 : i - 1));
  };
  const next = (e) => {
    e?.stopPropagation();
    setLightbox((i) => (i === photos.length - 1 ? 0 : i + 1));
  };

  return (
    <section
      id="galerij"
      data-testid="gallery-section"
      className="bg-brand-surface py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-20">
        <div className="mb-16 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <p className="overline">Galerij</p>
            <h2 className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight text-brand-ink sm:text-5xl lg:text-6xl text-balance">
              Wandel <span className="italic">virtueel</span> door het huis.
            </h2>
          </div>
          <p className="text-sm text-brand-muted">
            68 foto's beschikbaar · Klik op een foto om te vergroten
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:gap-4">
          {layout.map((item, i) => (
            <button
              key={i}
              data-testid={`gallery-tile-${i}`}
              onClick={() => open(item.idx)}
              className={`group relative overflow-hidden bg-brand-border ${item.span} ${item.aspect}`}
            >
              <img
                src={photos[item.idx]}
                alt={`Foto ${item.idx + 1} Kempenaar 29`}
                className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                loading="lazy"
              />
              {item.outdoor && (
                <SunFlare
                  intensity="soft"
                  side={i % 2 === 0 ? "top-right" : "top-left"}
                />
              )}
              <div className="absolute inset-0 bg-black/0 transition-colors duration-500 group-hover:bg-black/20" />
            </button>
          ))}
        </div>

        {/* Thumbnail strip */}
        <div className="mt-8 overflow-x-auto">
          <div className="flex gap-3 pb-2">
            {photos.slice(0, 24).map((p, i) => (
              <button
                key={i}
                data-testid={`thumb-${i}`}
                onClick={() => open(i)}
                className="aspect-[3/2] w-32 flex-shrink-0 overflow-hidden bg-brand-border"
              >
                <img
                  src={p}
                  alt={`Thumb ${i + 1}`}
                  className="h-full w-full object-cover transition-transform duration-500 hover:scale-110"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          data-testid="lightbox"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4"
          onClick={close}
        >
          <button
            data-testid="lightbox-close"
            onClick={close}
            className="absolute right-6 top-6 text-white hover:opacity-70"
          >
            <X size={32} />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={prev}
            className="absolute left-4 text-white hover:opacity-70 md:left-10"
          >
            <ChevronLeft size={48} />
          </button>
          <img
            src={photos[lightbox]}
            alt={`Foto ${lightbox + 1}`}
            className="max-h-[88vh] max-w-[92vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <Popcorn active={lightbox === NETFLIX_PHOTO_INDEX} />
          <button
            data-testid="lightbox-next"
            onClick={next}
            className="absolute right-4 text-white hover:opacity-70 md:right-10"
          >
            <ChevronRight size={48} />
          </button>
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-sm tracking-wider text-white/70">
            {lightbox + 1} / {photos.length}
          </p>
        </div>
      )}
    </section>
  );
}
